import type {
  FundingProfile,
  OrganizationDetails,
  ProfileObjectiveResponse,
} from "../../api/profile";
import type { OnboardingProfileData } from "./types";

export const createEmptyOnboardingProfile = (): OnboardingProfileData => ({
  organizationType: "",
  organizationName: "",
  country: "",
  postcode: "",
  operatingScope: "",
  whatWeDo: "",
  operatingYears: "",
  annualTurnoverBand: "",
  primaryBeneficiaries: [],
  focusThemes: [],
  fundingTypes: [],
  fundingAmountBand: "",
  fundingTimeline: "",
  websiteUrl: "",
  documentUploads: [],
});

const splitList = (value: unknown): string[] => {
  if (Array.isArray(value)) {
    return value.map((item) => String(item || "").trim()).filter(Boolean);
  }
  if (typeof value === "string") {
    return value
      .split(",")
      .map((item) => item.trim())
      .filter(Boolean);
  }
  return [];
};

export function mapProfileToOnboarding(
  details?: OrganizationDetails | null,
  objective?: ProfileObjectiveResponse | null,
  funding?: FundingProfile | null
): Partial<OnboardingProfileData> {
  const objectiveRecord =
    objective?.objective && typeof objective.objective === "object"
      ? (objective.objective as Record<string, any>)
      : {};
  const onboardingObjective = objectiveRecord?.onboarding_profile ?? {};
  const fundingExtraDetails = funding?.funding_extra_details ?? {};

  return {
    organizationType: details?.organization_type ?? "",
    organizationName: details?.company_name ?? "",
    country: details?.region ?? "",
    postcode: details?.postal_code ?? "",
    operatingScope:
      (details as any)?.operating_area ??
      (details?.extra_details as any)?.onboarding_operating_scope ??
      "",
    whatWeDo:
      objective?.project_description ??
      objectiveRecord?.mission ??
      objectiveRecord?.objective ??
      "",
    operatingYears:
      fundingExtraDetails?.years_operating ??
      (details?.extra_details as any)?.onboarding_operating_years ??
      "",
    annualTurnoverBand:
      fundingExtraDetails?.annual_turnover_band ??
      (details?.extra_details as any)?.annual_turnover_band ??
      "",
    primaryBeneficiaries:
      splitList(onboardingObjective?.primary_beneficiaries).length
        ? splitList(onboardingObjective?.primary_beneficiaries)
        : splitList(objectiveRecord?.beneficiary_groups),
    focusThemes:
      splitList(onboardingObjective?.focus_themes).length
        ? splitList(onboardingObjective?.focus_themes)
        : splitList(objectiveRecord?.area_focus).length
        ? splitList(objectiveRecord?.area_focus)
        : splitList(objective?.sector),
    fundingTypes: Array.isArray(funding?.funding_types) ? funding?.funding_types : [],
    fundingAmountBand:
      fundingExtraDetails?.onboarding_funding_amount_band ??
      (typeof funding?.funding_range === "object" ? funding?.funding_range?.range : "") ??
      "",
    fundingTimeline: fundingExtraDetails?.funding_timeline ?? "",
    websiteUrl: details?.website_url ?? "",
  };
}

export function mergeOnboardingIntoDetails(
  existing: OrganizationDetails | null | undefined,
  data: OnboardingProfileData
): OrganizationDetails {
  const seed = existing ?? ({} as OrganizationDetails);
  const extraDetails = {
    ...(seed.extra_details ?? {}),
    annual_turnover_band: data.annualTurnoverBand,
    onboarding_operating_scope: data.operatingScope,
  };

  return {
    company_name: data.organizationName || seed.company_name || "",
    entity_type: seed.entity_type ?? "Not Specified",
    organization_number: seed.organization_number ?? null,
    organization_type: data.organizationType || seed.organization_type || "",
    sector: seed.sector ?? "",
    region: data.country || seed.region || "",
    address_line: seed.address_line ?? null,
    city: seed.city ?? null,
    state: seed.state ?? null,
    postal_code: data.postcode || seed.postal_code || "",
    size: seed.size ?? null,
    founded_year: seed.founded_year ?? null,
    website_url: data.websiteUrl || seed.website_url || null,
    profile_photo: seed.profile_photo ?? null,
    extra_details: extraDetails,
    Number_of_Board_Members: seed.Number_of_Board_Members ?? null,
    Number_of_Employee: seed.Number_of_Employee ?? null,
    Number_of_Volunteers: seed.Number_of_Volunteers ?? null,
    contact_email: seed.contact_email ?? null,
    contact_phone_number: seed.contact_phone_number ?? null,
    phone_code: seed.phone_code ?? null,
    operating_area: data.operatingScope || seed.operating_area || "",
    rural_urban: seed.rural_urban ?? "",
    age: seed.age ?? null,
    gender: seed.gender ?? "",
    ethnicity: seed.ethnicity ?? "",
    disability_status: seed.disability_status ?? "",
    veteran: seed.veteran ?? "",
    immigration_status: seed.immigration_status ?? "",
    socioeconomic_status: seed.socioeconomic_status ?? "",
  };
}

export function mergeOnboardingIntoObjective(
  existing: ProfileObjectiveResponse | null | undefined,
  data: OnboardingProfileData
) {
  const objectiveRecord =
    existing?.objective && typeof existing.objective === "object"
      ? { ...(existing.objective as Record<string, any>) }
      : {};

  return {
    objective: {
      ...objectiveRecord,
      mission: data.whatWeDo || objectiveRecord?.mission || "",
      objective: data.whatWeDo || objectiveRecord?.objective || "",
      area_focus: data.focusThemes.join(", ") || objectiveRecord?.area_focus || "",
      beneficiary_groups:
        data.primaryBeneficiaries.join(", ") ||
        objectiveRecord?.beneficiary_groups ||
        "",
      sustainable_development_goal:
        objectiveRecord?.sustainable_development_goal || existing?.sector || "",
      organisation_history: objectiveRecord?.organisation_history || existing?.bio || "",
      onboarding_profile: {
        ...(objectiveRecord?.onboarding_profile ?? {}),
        what_we_do: data.whatWeDo,
        focus_themes: data.focusThemes,
        primary_beneficiaries: data.primaryBeneficiaries,
      },
    },
    project_description: data.whatWeDo || existing?.project_description || "",
    bio: existing?.bio || "",
    sector: data.focusThemes[0] || existing?.sector || "",
    org_activities_summary: existing?.org_activities_summary || "",
    problem_statement: existing?.problem_statement || "",
    who_is_affected: existing?.who_is_affected || "",
    evidence_of_need: existing?.evidence_of_need || "",
    beneficiaries_description: existing?.beneficiaries_description || "",
    beneficiaries_number: existing?.beneficiaries_number || 0,
    beneficiary_selection_method: existing?.beneficiary_selection_method || "",
    success_indicators: existing?.success_indicators || "",
    outcome_timescale: existing?.outcome_timescale || [],
    long_term_impact: existing?.long_term_impact || "",
    safeguarding_approach: existing?.safeguarding_approach || "",
    equality_inclusion_approach: existing?.equality_inclusion_approach || "",
    data_collection_methods: existing?.data_collection_methods || [],
  };
}

export function mergeOnboardingIntoFunding(
  existing: FundingProfile | null | undefined,
  data: OnboardingProfileData
): FundingProfile {
  const seed = existing ?? {};
  const extraDetails = {
    ...(seed.funding_extra_details ?? {}),
    years_operating: data.operatingYears || seed.funding_extra_details?.years_operating || "",
    annual_turnover_band:
      data.annualTurnoverBand || seed.funding_extra_details?.annual_turnover_band || "",
    onboarding_funding_amount_band:
      data.fundingAmountBand ||
      seed.funding_extra_details?.onboarding_funding_amount_band ||
      "",
    funding_timeline: data.fundingTimeline || seed.funding_extra_details?.funding_timeline || "",
  };

  return {
    ...seed,
    funding_types: data.fundingTypes.length ? data.fundingTypes : seed.funding_types ?? [],
    funding_extra_details: extraDetails,
  };
}
